import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist } from 'zustand/middleware';
import { DateTime } from 'luxon';

import { BasicLogger } from '@initia-digital/ts-utils';
import { IPlan, IPrestart } from '@initia-digital/types/work-and-safety';


export const DEFAULT_CREATE_PLAN_STATE: IPlan = {
    id: '',
    status: "DRAFT",
    objectives: [],
    overrideSiteAddress: null,
    ourContact: null,
    startDate: DateTime.local().toISO(),
    endDate: DateTime.local().plus({ day: 1 }).toISO(),
    title: "",
    description: "",
    supportingContext: "",
    teamMembers: [],
    serviceClearanceRequired: false,
    testsRequired: false,
    testSchedule: {
        rows: []
    },
    installationsRequired: false,
    installationSchedule: {
        rows: []
    },
    reinstatementRequirements: null,
    surveyRequirements: null,
    contractorsAndSubcontractors: [],
    bookings: {
        rows: []
    },
    siteContactId: null,
    contacts: {
        rows: []
    },
    fileUploads: [],
    criticalHazards: [],
    hazardsFromRegister: [],
    equipmentNotes: '',
    equipment: [],
    reviewNotes: []
}

export const DEFAULT_CREATE_PRESTART_STATE: IPrestart = {
    id: '',
    status: 'DRAFT',
    prestartTimestamp: DateTime.local().toISO(),
    leadingTeam: false,
    parentPrestartId: '',
    continuationDetail: '',
    contractorsAndSubcontractors: [],
    siteContacts: {
        rows: []
    },
    teamAwareOfHighlightedHazards: false,
    needToAddTeamsHighlightedHazards: false,
    teamsHighlightedHazards: [],
    newHazardsAndControls: [],
    hazardObservationMedia: [],
    teamWorkDetail: '',
    otherPrestartMedia: [],
    otherNotes: ''
}

export const DEFAULT_CREATE_REVIEW_COMMENT_STATE = "";

export interface IWorkAndSafetyState {
    logger: BasicLogger
    updateLogger: (logger: BasicLogger) => void
    isSideDrawerOpen: boolean
    setIsSideDrawerOpen: (isSideDrawerOpen: boolean) => void
    editPlanState: IPlan
    resetEditPlanState: (jobId: string) => void
    updateEditPlanState: (updatedPlan: IPlan) => void
    editPrestartState: IPrestart
    resetEditPrestartState: (jobId: string, planId: string) => void
    updateEditPrestartState: (updatedPrestart: IPrestart) => void
    editNextReviewCommentState: string
    setNewReviewComment: (editNextReviewCommentState: string) => void
}


export const useWorkAndSafetyStore = create<IWorkAndSafetyState>()(
    devtools(
        persist(
            immer(
                (set) => ({
                    logger: new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' }),
                    updateLogger: (logger: BasicLogger) => set({ logger }),
                    isSideDrawerOpen: false,
                    setIsSideDrawerOpen: (isSideDrawerOpen: boolean) => set({ isSideDrawerOpen }),
                    editPlanState: DEFAULT_CREATE_PLAN_STATE,
                    resetEditPlanState: (jobId: string) => set({ editPlanState: { ...DEFAULT_CREATE_PLAN_STATE, jobId } }),
                    updateEditPlanState: (updatedPlan: IPlan) => set(state => {
                        // ---------------------
                        state.editPlanState = updatedPlan;
                    }),
                    editPrestartState: DEFAULT_CREATE_PRESTART_STATE,
                    resetEditPrestartState: (jobId: string, planId: string) => set({ editPrestartState: { ...DEFAULT_CREATE_PRESTART_STATE, jobId, planId } }),
                    updateEditPrestartState: (updatedPrestart: IPrestart) => set(state => {
                        // ---------------------
                        state.editPrestartState = updatedPrestart;
                    }),
                    editNextReviewCommentState: DEFAULT_CREATE_REVIEW_COMMENT_STATE,
                    setNewReviewComment: (editNextReviewCommentState: string) => set(state => {
                        // ---------------------
                        state.editNextReviewCommentState = editNextReviewCommentState;
                    })
                })
            ),
            {
                name: 'work-and-safety-store',
                partialize: state => ({
                    editPlanState: state.editPlanState,
                    editPrestartState: state.editPrestartState,
                    editNextReviewCommentState: state.editNextReviewCommentState
                })
            }
        )
    )
);